/**
 * Client HTTP de l'API.
 *
 * Choix de sécurité côté navigateur :
 *
 * - Aucun jeton d'authentification n'est stocké en JavaScript. La session vit
 *   dans un cookie `HttpOnly` posé par le serveur : une faille XSS ne peut donc
 *   pas l'exfiltrer. Toutes les requêtes utilisent `credentials: "include"`.
 * - La protection CSRF repose sur un jeton à double soumission : le serveur le
 *   place dans un cookie lisible, et le client le renvoie dans l'en-tête
 *   `X-CSRF-Token`. Un site tiers ne peut pas lire ce cookie, donc il ne peut
 *   pas forger la requête.
 * - Les erreurs du serveur arrivent toujours dans la même enveloppe, et sont
 *   converties en `ApiError` avec un message déjà rédigé en français.
 */

const BASE_PATH = "/api/v1";
const CSRF_COOKIE = "arkv_csrf";
const CSRF_HEADER = "X-CSRF-Token";
const BUSINESS_HEADER = "X-Business-Id";

export type ApiErrorCode =
  | "validation_error"
  | "authentication_required"
  | "twofa_required"
  | "reauthentication_required"
  | "permission_denied"
  | "csrf_failed"
  | "not_found"
  | "conflict"
  | "rate_limited"
  | "payload_too_large"
  | "internal_error"
  | "network_error"
  | string;

export interface ApiErrorDetail {
  field?: string;
  message: string;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: ApiErrorCode;
  readonly details?: ApiErrorDetail[] | Record<string, unknown>;
  readonly retryAfterSeconds?: number;

  constructor(
    message: string,
    options: {
      status: number;
      code: ApiErrorCode;
      details?: ApiErrorDetail[] | Record<string, unknown>;
      retryAfterSeconds?: number;
    },
  ) {
    super(message);
    this.name = "ApiError";
    this.status = options.status;
    this.code = options.code;
    this.details = options.details;
    this.retryAfterSeconds = options.retryAfterSeconds;
  }

  /** Messages par champ, pour afficher l'erreur au bon endroit du formulaire. */
  fieldErrors(): Record<string, string> {
    const out: Record<string, string> = {};
    if (Array.isArray(this.details)) {
      for (const detail of this.details) {
        if (detail.field) out[detail.field] = detail.message;
      }
    }
    return out;
  }

  get requiresTwoFactor(): boolean {
    return this.code === "twofa_required";
  }

  get requiresReauth(): boolean {
    return this.code === "reauthentication_required";
  }

  get isUnauthenticated(): boolean {
    return this.code === "authentication_required" || this.status === 401;
  }
}

function readCookie(name: string): string | null {
  const prefix = `${name}=`;
  for (const part of document.cookie.split(";")) {
    const trimmed = part.trim();
    if (trimmed.startsWith(prefix)) {
      return decodeURIComponent(trimmed.slice(prefix.length));
    }
  }
  return null;
}

/**
 * Entreprise courante.
 *
 * Mémorisée en `localStorage` : c'est une simple préférence d'affichage, pas un
 * élément de sécurité — le serveur vérifie de toute façon l'appartenance à
 * chaque requête.
 */
const BUSINESS_STORAGE_KEY = "arkvibes.currentBusinessId";

export function getCurrentBusinessId(): string | null {
  try {
    return window.localStorage.getItem(BUSINESS_STORAGE_KEY);
  } catch {
    return null;
  }
}

export function setCurrentBusinessId(id: string | null): void {
  try {
    if (id) window.localStorage.setItem(BUSINESS_STORAGE_KEY, id);
    else window.localStorage.removeItem(BUSINESS_STORAGE_KEY);
  } catch {
    /* mode privé ou stockage refusé : on continue sans mémoriser */
  }
}

export interface RequestOptions {
  method?: "GET" | "POST" | "PATCH" | "PUT" | "DELETE";
  body?: unknown;
  formData?: FormData;
  query?: Record<string, string | number | boolean | string[] | undefined | null>;
  /** Réponse binaire (téléchargement de fichier ou d'archive). */
  raw?: boolean;
  signal?: AbortSignal;
}

export interface BinaryResponse {
  blob: Blob;
  filename: string;
  sha256?: string;
}

function buildQuery(
  query: RequestOptions["query"],
): string {
  if (!query) return "";
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === "") continue;
    if (Array.isArray(value)) {
      for (const item of value) params.append(key, String(item));
    } else {
      params.append(key, String(value));
    }
  }
  const serialized = params.toString();
  return serialized ? `?${serialized}` : "";
}

function filenameFromDisposition(header: string | null, fallback: string): string {
  if (!header) return fallback;
  const utf8 = /filename\*=UTF-8''([^;]+)/i.exec(header);
  if (utf8?.[1]) {
    try {
      return decodeURIComponent(utf8[1]);
    } catch {
      /* on retombe sur la variante ASCII */
    }
  }
  const ascii = /filename="?([^";]+)"?/i.exec(header);
  return ascii?.[1] ?? fallback;
}

async function parseError(response: Response): Promise<ApiError> {
  const retryAfter = response.headers.get("retry-after");
  let code: ApiErrorCode = "internal_error";
  let message = "Une erreur est survenue.";
  let details: ApiErrorDetail[] | Record<string, unknown> | undefined;

  try {
    const payload = await response.json();
    if (payload?.error) {
      code = payload.error.code ?? code;
      message = payload.error.message ?? message;
      details = payload.error.details;
    }
  } catch {
    message =
      response.status >= 500
        ? "Le serveur a rencontré une erreur. Réessayez dans un instant."
        : `Requête refusée (code ${response.status}).`;
  }

  return new ApiError(message, {
    status: response.status,
    code,
    details,
    retryAfterSeconds: retryAfter ? Number(retryAfter) : undefined,
  });
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = "GET", body, formData, query, raw, signal } = options;
  const headers: Record<string, string> = { Accept: "application/json" };

  if (method !== "GET") {
    const csrf = readCookie(CSRF_COOKIE);
    if (csrf) headers[CSRF_HEADER] = csrf;
  }
  const businessId = getCurrentBusinessId();
  if (businessId) headers[BUSINESS_HEADER] = businessId;

  let payload: BodyInit | undefined;
  if (formData) {
    // Pas de Content-Type : le navigateur ajoute la frontière multipart.
    payload = formData;
  } else if (body !== undefined) {
    headers["Content-Type"] = "application/json";
    payload = JSON.stringify(body);
  }

  let response: Response;
  try {
    response = await fetch(`${BASE_PATH}${path}${buildQuery(query)}`, {
      method,
      headers,
      body: payload,
      credentials: "include",
      signal,
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") throw error;
    throw new ApiError(
      "Impossible de joindre le serveur. Vérifiez votre connexion.",
      { status: 0, code: "network_error" },
    );
  }

  if (!response.ok) throw await parseError(response);

  if (raw) {
    const blob = await response.blob();
    return {
      blob,
      filename: filenameFromDisposition(
        response.headers.get("content-disposition"),
        "fichier",
      ),
      sha256: response.headers.get("x-archive-sha256") ?? undefined,
    } as T;
  }

  if (response.status === 204) return undefined as T;
  const text = await response.text();
  if (!text) return undefined as T;
  return JSON.parse(text) as T;
}

export const api = {
  get: <T>(path: string, options?: Omit<RequestOptions, "method" | "body">) =>
    request<T>(path, { ...options, method: "GET" }),
  post: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, "method">) =>
    request<T>(path, { ...options, method: "POST", body }),
  patch: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, "method">) =>
    request<T>(path, { ...options, method: "PATCH", body }),
  put: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, "method">) =>
    request<T>(path, { ...options, method: "PUT", body }),
  delete: <T>(path: string, options?: Omit<RequestOptions, "method" | "body">) =>
    request<T>(path, { ...options, method: "DELETE" }),
  upload: <T>(path: string, formData: FormData) =>
    request<T>(path, { method: "POST", formData }),
  download: (path: string, options?: Omit<RequestOptions, "method" | "raw">) =>
    request<BinaryResponse>(path, { ...options, method: "GET", raw: true }),
};

/** Déclenche l'enregistrement d'un fichier reçu de l'API. */
export function saveBlob(response: BinaryResponse): void {
  const url = URL.createObjectURL(response.blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = response.filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Laisser au navigateur le temps de démarrer le téléchargement.
  window.setTimeout(() => URL.revokeObjectURL(url), 10_000);
}
